import type { ComposeInput, ComposeOutput, FontRef } from '@proofroom/schema';

export interface ComposeResult { output: ComposeOutput; ms: number }

export class EngineUnavailableError extends Error {
  constructor(reason: string) { super(`Composition engine unavailable: ${reason}`); this.name = 'EngineUnavailableError'; }
}

/**
 * @proofroom/engine's browser bundle (packages/engine/src/index.ts):
 *   createEngine({ fontProvider }) -> { prepare(fonts): Promise<void>; compose(input): ComposeOutput }
 * `prepare` must run before `compose`; both throw for a face that is not loaded, and nothing here
 * composes any other way. The portal shows the error instead of a substitute face.
 */
export interface EngineLike { compose(input: ComposeInput): Promise<ComposeOutput> }
interface RawEngine {
  prepare(fonts: Array<Partial<FontRef> & { id: string }>): Promise<void>;
  compose(input: ComposeInput): ComposeOutput;
}
interface EngineModule {
  createEngine?: (opts: { fontProvider: unknown }) => RawEngine;
  createUrlFontProvider?: (baseUrl: string) => unknown;
  default?: EngineModule;
}

/** Served faces only, as TTF: HarfBuzz parses raw sfnt tables, never WOFF2. */
export const engineFontRefs = (input: ComposeInput): Array<Partial<FontRef> & { id: string }> =>
  (input.fonts ?? []).filter((f) => f.status !== 'missing' && f.url).map((f) => ({ ...f, url: f.url!.replace(/\.woff2$/, '.ttf') }));

export const ENGINE_URL = '/engine/engine.js';
export const HB_WASM_URL = '/engine/hb.wasm';

function wrap(mod: EngineModule): EngineLike {
  const m = mod.createEngine ? mod : mod.default ?? mod;
  if (!m.createEngine || !m.createUrlFontProvider) throw new EngineUnavailableError('bundle has no createEngine');
  const engine = m.createEngine({ fontProvider: m.createUrlFontProvider('/fonts') });
  const prepared = new Set<string>();
  return {
    async compose(input) {
      const refs = engineFontRefs(input).filter((f) => !prepared.has(f.id));
      if (refs.length) {
        await engine.prepare(refs);
        for (const f of refs) prepared.add(f.id);
      }
      return engine.compose(input);
    },
  };
}

let enginePromise: Promise<EngineLike> | undefined;

/** Load the engine bundle once. Rejects with EngineUnavailableError when it is not served. */
export function loadEngine(fetchImpl: typeof fetch = fetch): Promise<EngineLike> {
  if (enginePromise) return enginePromise;
  enginePromise = (async () => {
    const head = await fetchImpl(ENGINE_URL, { method: 'HEAD' }).catch((e: Error) => { throw new EngineUnavailableError(e.message); });
    if (!head.ok) throw new EngineUnavailableError(`${ENGINE_URL} answered ${head.status}; build @proofroom/engine`);
    (globalThis as { PROOFROOM_HB_WASM_URL?: string }).PROOFROOM_HB_WASM_URL ??= new URL(HB_WASM_URL, location.origin).href;
    const mod = (await import(/* @vite-ignore */ ENGINE_URL)) as EngineModule;
    return wrap(mod);
  })();
  enginePromise.catch(() => { enginePromise = undefined; });
  return enginePromise;
}

export async function composeHere(input: ComposeInput, fetchImpl: typeof fetch = fetch): Promise<ComposeResult> {
  const t0 = performance.now();
  const engine = await loadEngine(fetchImpl);
  const output = await engine.compose(input);
  return { output, ms: performance.now() - t0 };
}
